import { Clock, GitCommit, Plus, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { getPolicyHistory } from '@/services/documentPolicyService';

import type { PolicyHistoryEntry } from '@/types/documentPolicy.types';

interface PolicyHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyId: string;
  policyName: string;
}

function formatChangeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value ?? '—');
}

function ActionIcon({ action }: { action: string }) {
  switch (action) {
    case 'created':
      return <Plus className='h-4 w-4' />;
    case 'cloned':
      return <RefreshCw className='h-4 w-4' />;
    default:
      return <GitCommit className='h-4 w-4' />;
  }
}

function actionColor(action: string): string {
  switch (action) {
    case 'created':
      return 'bg-green-100 text-green-700';
    case 'cloned':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-blue-100 text-blue-700';
  }
}

export function PolicyHistoryModal({ isOpen, onClose, policyId, policyName }: PolicyHistoryModalProps) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<PolicyHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    setError(null);

    getPolicyHistory(policyId)
      .then(setEntries)
      .catch(() => setError(t('documentPolicies.history.noHistory')))
      .finally(() => setIsLoading(false));
  }, [isOpen, policyId, t]);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 flex max-h-[80vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <div className='flex items-center gap-2'>
            <Clock className='h-5 w-5 text-gray-400' />
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>{t('documentPolicies.history.title')}</h2>
              <p className='text-sm text-gray-500'>{policyName}</p>
            </div>
          </div>
          <button onClick={onClose} className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {isLoading ? (
            <div className='flex justify-center py-12'>
              <div className='h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
            </div>
          ) : error ? (
            <p className='py-8 text-center text-sm text-gray-500'>{error}</p>
          ) : entries.length === 0 ? (
            <p className='py-8 text-center text-sm text-gray-500'>{t('documentPolicies.history.noHistory')}</p>
          ) : (
            <div className='relative'>
              <div className='absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200' />
              <div className='space-y-6'>
                {entries.map((entry) => {
                  const changeKeys = Object.keys(entry.changes);
                  return (
                    <div key={entry.id} className='relative flex gap-4'>
                      <div
                        className={cn(
                          'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                          actionColor(entry.action),
                        )}>
                        <ActionIcon action={entry.action} />
                      </div>
                      <div className='flex-1 rounded-lg border border-gray-200 bg-white p-4'>
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium text-gray-900'>
                            {t(`documentPolicies.history.actions.${entry.action}`)}
                          </span>
                          <span className='text-xs text-gray-500'>
                            {new Date(entry.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        <p className='mt-1 text-xs text-gray-500'>
                          {t('documentPolicies.history.by')} {entry.actor.name}
                        </p>
                        {changeKeys.length > 0 ? (
                          <div className='mt-3 space-y-2'>
                            {changeKeys.map((field) => {
                              const change = entry.changes[field]!;
                              return (
                                <div key={field} className='rounded bg-gray-50 px-3 py-2 text-xs'>
                                  <span className='font-medium text-gray-700'>{field}</span>
                                  <div className='mt-1 flex items-center gap-2 text-gray-500'>
                                    <span className='line-through'>{formatChangeValue(change.from)}</span>
                                    <span className='text-gray-400'>&rarr;</span>
                                    <span className='font-medium text-gray-900'>{formatChangeValue(change.to)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
